import React, { useEffect, useState } from "react";
import { Badge, Spinner, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserShield,
  faEnvelope,
  faIdCard,
  faClock,
  faUsers,
  faCheckCircle,
  faArrowLeft,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { InvisilatorService } from "Services/InvisilatorService";
import { useParams, useNavigate } from "react-router-dom";
import TablePagination from "components/Pagination/Table.paginaition";

export default function ViewInvigilatorPage() {
  const { invigilatorId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await InvisilatorService.getInvigilatorById(invigilatorId);

      if (res?.status === 200) {
        setData(res.data?.data?.invigilator);
        const totalCount = res.data?.data?.count || 0;
        setTotal(totalCount);
      }
    } catch (err) {
      console.error("Failed to fetch invigilator details", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (invigilatorId) {
      fetchDetails();
    }
  }, [invigilatorId]);

  return (
    <div className="container-fluid py-4">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">
            <FontAwesomeIcon
              icon={faUserShield}
              className="me-2 text-primary"
            />
            View Invigilator
          </h4>
          <small className="text-muted">
            Detailed information of invigilator
          </small>
        </div>

        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Button>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* BASIC INFO */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body row g-4">
              <div className="col-md-6">
                <small className="text-muted">Invigilator ID</small>
                <div className="fw-bold fs-6">
                  <FontAwesomeIcon
                    icon={faIdCard}
                    className="me-2 text-secondary"
                  />
                  {data.invigilatorId}
                </div>
              </div>

              <div className="col-md-6">
                <small className="text-muted">Name</small>
                <div className="fw-bold fs-6">{data.name}</div>
              </div>

              <div className="col-md-6">
                <small className="text-muted">Email</small>
                <div>
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="me-2 text-secondary"
                  />
                  {data.email}
                </div>
              </div>

              <div className="col-md-6">
                <small className="text-muted">Role</small>
                <br />
                <Badge bg="secondary" className="px-3 py-2">
                  {data.role}
                </Badge>
              </div>
            </div>
          </div>

          {/* TRUE PERMISSIONS */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Enabled Permissions</h6>

              <div className="d-flex flex-wrap gap-2">
                {Object.entries(data.permissions || {})
                  .filter(([_, v]) => v)
                  .map(([key]) => (
                    <Badge
                      key={key}
                      bg="success-subtle"
                      text="success"
                      className="border px-3 py-2 fw-semibold"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                      {key.replace("can", "")}
                    </Badge>
                  ))}
              </div>
            </div>
          </div>

          {/* FALSE PERMISSIONS (NEW CARD) */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h6 className="fw-bold mb-3 ">Disabled Permissions</h6>

              {Object.entries(data.permissions || {}).filter(([_, v]) => !v)
                .length === 0 ? (
                <div className="text-muted fst-italic">
                  This invigilator has no disabled permissions.
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(data.permissions || {})
                    .filter(([_, v]) => !v)
                    .map(([key]) => (
                      <Badge
                        key={key}
                        bg="danger-subtle"
                        text="danger"
                        className="border px-3 py-2 fw-semibold"
                      >
                        <FontAwesomeIcon
                          icon={faTimesCircle}
                          className="me-1"
                        />
                        {key.replace("can", "")}
                      </Badge>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* STATUS SECTION */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body row g-4">
              <div className="col-md-4">
                <small className="text-muted">Assigned Students</small>
                <div className="fw-bold fs-6">
                  <FontAwesomeIcon
                    icon={faUsers}
                    className="me-2 text-secondary"
                  />
                  {data.assignedStudents?.length || 0}
                </div>
              </div>

              <div className="col-md-4">
                <small className="text-muted">Status</small>
                <br />
                <Badge
                  bg={data.status === "ACTIVE" ? "success" : "danger"}
                  className="px-3 py-2 fw-semibold"
                >
                  {data.status}
                </Badge>
              </div>

              <div className="col-md-4">
                <small className="text-muted">Last Login</small>
                <div>
                  <FontAwesomeIcon
                    icon={faClock}
                    className="me-2 text-secondary"
                  />
                  --
                </div>
              </div>
            </div>
          </div>

          {/* STUDENTS TABLE */}
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Assigned Student Details</h6>

              {data.assignedStudents?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Username</th>
                        <th>Registration No</th>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Roll Number</th>
                      </tr>
                    </thead>

                    <tbody>
                      {data.assignedStudents.map((stu: any, index: number) => (
                        <tr key={stu._id || index}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold">{stu.username}</td>
                          <td>{stu.registrationNumber}</td>
                          <td>{stu.email}</td>
                          <td>{stu.name}</td>
                          <td>{stu.rollNumber}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <TablePagination
                    total={total}
                    currentPage={currentPage}
                    perPage={perPage}
                    handlePageChange={(page: number) => setCurrentPage(page)}
                    setPerPage={(size: number) => {
                      setPerPage(size);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              ) : (
                <div className="text-muted fst-italic">
                  No students assigned
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
